import React, { useState } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";
import CropsList from "./CropList";
import CorrectIcon from "./CorrectIcon";
import {
  BG_STYLES,
  BottomDrawer,
  COLORS,
  COMMON_STYLES,
  TEXT_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from "@dehaat/dds";
import { Crop } from "../models/diagnosis";
import { rnNavigation } from "app/utils/appConfig";
import { ROUTES as FARMER_360_ROUTES } from "app/farmer360/routes/types";
import SelectUser from "./SelectUser";
import ImageSourceDialog from "./modals/ImageSourceDialog";
import { trackEvent } from "app/farmer360/utils/tracking";
import { CustomEventTypes } from "../utils/events/CustomEventTypes";
import { useAppDispatch, useAppSelector } from "app/hooks/reduxHooks";
import {
  selectFarmerName,
  selectFarmerPhone,
  selectPlantixUId,
  setFarmerId,
  setFarmerName,
  setFarmerPhone,
} from "app/farmer360/store/app";
import { useTranslation } from "react-i18next";
import getClientCode from "app/farmer360/utils/helpers/getClientCode";
import { getCookieValue } from "app/farmer360/utils/helper";
import {
  ASSUMED_USER_NAME,
  ASSUMED_USER_NUMBER,
  NAVIGATION_FROM_APP_CODE,
} from "app/farmer360/constants/common";
import { APP_CODE_TYPE } from "app/farmer360/models/auth";
import fetchOrCreateUser from "../apis/fetchOrCreateUser";

interface CropSelectionProps {
  selectedCropId?: number | null;
  handleAddCrop: (arg: Crop) => void;
  detectedCrops: Crop[];
  addCropFromCamera: () => void;
  addCropFromGalary: () => void;
}

const CropSelection = ({
  selectedCropId,
  handleAddCrop,
  detectedCrops,
  addCropFromCamera,
  addCropFromGalary,
}: CropSelectionProps) => {
  const [showSelectUser, SetShowSelectUser] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const plantixId = useAppSelector(selectPlantixUId);
  const farmerName = useAppSelector(selectFarmerName);
  const farmerPhone = useAppSelector(selectFarmerPhone);
  const { t } = useTranslation();
  const clientCode = getClientCode(
    getCookieValue(NAVIGATION_FROM_APP_CODE) as APP_CODE_TYPE
  );
  const clientName = getCookieValue(ASSUMED_USER_NAME);
  const clientNumber = getCookieValue(ASSUMED_USER_NUMBER);
  const dispatch = useAppDispatch();
  const handleAddFarmer = () => {
    trackEvent(CustomEventTypes.CropDocImageUserTypeSelected, {
      UserType: "other farmer",
    });
    SetShowSelectUser(false);
    rnNavigation({
      name: FARMER_360_ROUTES.ADD_FARMER,
    });
  };

  const onClickStartDiagnosisBtn = async () => {
    trackEvent(CustomEventTypes.CropDocImageCropSelectionCropNotFound, {
      PlantixID: plantixId,
    });
    if (clientCode === "02") {
      trackEvent(CustomEventTypes.CropDocImageUserTypeSelected, {
        UserType: "other farmer",
      });
      rnNavigation({
        name: FARMER_360_ROUTES.ADD_FARMER,
      });
    } else if (clientName && clientNumber) {
      trackEvent(CustomEventTypes.CropDocImageUserTypeSelected, {
        UserType: "other farmer",
      });
      const response = await fetchOrCreateUser(clientNumber, clientName);
      if (response) {
        dispatch(setFarmerId(response.id));
        dispatch(setFarmerName(response.full_name));
        dispatch(setFarmerPhone(response.phone_number));
      }
      setShowDialog(true);
    } else {
      SetShowSelectUser(true);
    }
  };

  return (
    <>
      <View style={[COMMON_STYLES.p1, BG_STYLES.bgWhite]}>
        <CropsList
          cropList={detectedCrops}
          icon={
            <CorrectIcon
              height={15}
              width={15}
              fill={COLORS.white}
              stroke={COLORS.white}
            />
          }
          selectedCropId={selectedCropId}
          iconContainerStyle={styles.tickStyle}
          handleImageClick={(crop) => handleAddCrop(crop)}
          imageStyle={styles.image}
        />
        <View
          style={[
            COMMON_STYLES["mt3/2"],
            COMMON_STYLES.row,
            COMMON_STYLES.justifyBetween,
            COMMON_STYLES.alignItemsCenter,
          ]}
        >
          <Text
            style={[
              TYPOGRAPHY.TEXT1,
              VARIANTS_STYLES.BASE,
              TEXT_STYLES.textBlack,
            ]}
          >
            {t("crop_not_listed")}
          </Text>
          <Pressable onPress={onClickStartDiagnosisBtn}>
            <Text
              style={[
                TYPOGRAPHY.TEXT2,
                VARIANTS_STYLES.SEMI_EMPHASIZED,
                TEXT_STYLES.textPrimary100,
              ]}
            >
              {t("start_new_diagnosis")}
            </Text>
          </Pressable>
        </View>
      </View>
      {showSelectUser && (
        <BottomDrawer
          onClose={() => SetShowSelectUser(false)}
          visible={showSelectUser}
          heading={t("who_are_you_diagnosing_for")}
        >
          <SelectUser
            handleUpload={() => {
              trackEvent(CustomEventTypes.CropDocImageUserTypeSelected, {
                UserType: "self",
              });
              trackEvent(CustomEventTypes.CropDocImageFarmerAdded, {
                UserType: "self",
                farmername: farmerName,
                farmermobile: farmerPhone,
              });
              SetShowSelectUser(false);
              setShowDialog(true);
            }}
            handleAddFarmer={handleAddFarmer}
          />
        </BottomDrawer>
      )}
      {showDialog && (
        <ImageSourceDialog
          visible={showDialog}
          onClose={() => setShowDialog(false)}
          uploadFromCamera={() => {
            trackEvent(CustomEventTypes.CropDocImageSourceSelected, {
              selectiontype: "camera",
            });
            setShowDialog(false);
            addCropFromCamera();
          }}
          uploadFromGallery={() => {
            trackEvent(CustomEventTypes.CropDocImageSourceSelected, {
              selectiontype: "gallery",
            });
            setShowDialog(false);
            addCropFromGalary();
          }}
        />
      )}
    </>
  );
};

const styles = StyleSheet.create({
  categoryTitle: {
    color: COLORS.neutral80,
  },

  image: {
    height: 80,
    width: 80,
  },

  tickStyle: {
    backgroundColor: COLORS.primary100,
    borderRadius: 9999,
    padding: 5,
    position: "absolute",
    right: 0,
    top: 0,
    zIndex: 1,
  },
});

export default CropSelection;
